import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HiUserCircle,
  HiShieldCheck,
  HiDocumentText,
  HiKey,
  HiArrowPath,
  HiExclamationTriangle,
  HiClock,
  HiChevronDown,
  HiChevronUp,
} from 'react-icons/hi2';

const EVENT_CONFIG = {
  userActivity: {
    icon: HiUserCircle,
    label: 'User Activity',
    color: 'blue',
    bgClass: 'bg-blue-900/50',
    textClass: 'text-blue-300',
    dotClass: 'bg-blue-500',
  },
  securityLog: {
    icon: HiShieldCheck,
    label: 'Security',
    color: 'amber',
    bgClass: 'bg-amber-900/50',
    textClass: 'text-amber-300',
    dotClass: 'bg-amber-500',
  },
  stageAudit: {
    icon: HiArrowPath,
    label: 'Stage Change',
    color: 'violet',
    bgClass: 'bg-violet-900/50',
    textClass: 'text-violet-300',
    dotClass: 'bg-violet-500',
  },
  documentView: {
    icon: HiDocumentText,
    label: 'Document View',
    color: 'emerald',
    bgClass: 'bg-emerald-900/50',
    textClass: 'text-emerald-300',
    dotClass: 'bg-emerald-500',
  },
  login: {
    icon: HiKey,
    label: 'Login / OTP',
    color: 'green',
    bgClass: 'bg-green-900/50',
    textClass: 'text-green-300',
    dotClass: 'bg-green-500',
  },
  default: {
    icon: HiClock,
    label: 'Event',
    color: 'slate',
    bgClass: 'bg-slate-700',
    textClass: 'text-slate-300',
    dotClass: 'bg-slate-500',
  },
};

const getEventConfig = source => EVENT_CONFIG[source] || EVENT_CONFIG.default;

const formatRelativeTime = date => {
  if (!date) return 'Unknown';
  const d = date instanceof Date ? date : new Date(date);
  const now = new Date();
  const sec = Math.floor((now - d) / 1000);
  const min = Math.floor(sec / 60);
  const hr = Math.floor(min / 60);
  const day = Math.floor(hr / 24);
  if (sec < 60) return 'Just now';
  if (min < 60) return `${min}m ago`;
  if (hr < 24) return `${hr}h ago`;
  if (day < 7) return `${day}d ago`;
  return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
};

const ActivityTimeline = ({ items = [], error = null, live = true, viewMode = 'timeline' }) => {
  const [expandedId, setExpandedId] = useState(null);
  const [filterSource, setFilterSource] = useState('all');

  const toDate = v => {
    if (!v) return null;
    if (v?.toMillis) return new Date(v.toMillis());
    if (v instanceof Date) return v;
    return new Date(v);
  };

  if (error) {
    return (
      <div className="rounded-lg border border-red-900/50 bg-red-950/20 p-4 text-sm text-red-300">
        <div className="flex items-center gap-2">
          <HiExclamationTriangle className="h-5 w-5 shrink-0" />
          Failed to load activity: {error}
        </div>
      </div>
    );
  }

  if (!items || items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-700 py-16 text-center">
        <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-slate-700/50">
          <HiClock className="h-7 w-7 text-slate-500" />
        </div>
        <p className="text-sm font-medium text-slate-400">No recent activity</p>
        <p className="mt-1 max-w-xs text-xs text-slate-500">
          Logins, document views, security events, and other actions will appear here as they occur.
        </p>
      </div>
    );
  }

  const sorted = [...items]
    .filter(i => {
      const ts = i.createdAt ?? i.timestamp ?? i.affectedAt;
      return ts;
    })
    .sort((a, b) => {
      const aTs = toDate(a.createdAt ?? a.timestamp ?? a.affectedAt);
      const bTs = toDate(b.createdAt ?? b.timestamp ?? b.affectedAt);
      return (bTs || 0) - (aTs || 0);
    });

  const filtered =
    filterSource === 'all' ? sorted : sorted.filter(i => (i.source || 'unknown') === filterSource);

  const sources = [...new Set(sorted.map(i => i.source).filter(Boolean))];

  const renderEventContent = (item, index) => {
    const source = item.source || 'unknown';
    const cfg = getEventConfig(source);
    const Icon = cfg.icon;
    const ts = item.createdAt ?? item.timestamp ?? item.affectedAt;
    const date = toDate(ts);
    const relativeTime = formatRelativeTime(date);
    const absoluteTime = date
      ? date.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'medium' })
      : 'Unknown';
    const eventText =
      item.action ||
      item.eventType ||
      item.message ||
      item.reason ||
      (item.previousStage && item.newStage ? `${item.previousStage} → ${item.newStage}` : null) ||
      (item.viewerName ? `Viewed by ${item.viewerName}` : null) ||
      'Activity event';
    const userLabel =
      item.userEmail ||
      item.email ||
      item.viewerEmail ||
      (item.userId ? `User ${item.userId.slice(0, 8)}…` : null);
    return { source, cfg, Icon, relativeTime, absoluteTime, eventText, userLabel, item, index };
  };

  if (viewMode === 'table') {
    return (
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          {live && (
            <span className="flex items-center gap-1.5 rounded-full bg-green-900/40 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-green-400">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-green-400" />
              Live
            </span>
          )}
          <span className="text-xs text-slate-500">{filtered.length} events</span>
          {sources.length > 1 && (
            <div className="flex flex-wrap gap-1.5">
              <button
                onClick={() => setFilterSource('all')}
                className={`rounded-full px-2.5 py-1 text-[10px] font-medium ${filterSource === 'all' ? 'bg-slate-600 text-white' : 'bg-slate-800/50 text-slate-500 hover:bg-slate-700'}`}
              >
                All
              </button>
              {sources.map(src => {
                const c = getEventConfig(src);
                return (
                  <button
                    key={src}
                    onClick={() => setFilterSource(src)}
                    className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-medium ${
                      filterSource === src
                        ? `${c.bgClass} ${c.textClass}`
                        : 'bg-slate-800/50 text-slate-500 hover:bg-slate-700'
                    }`}
                  >
                    <c.icon className="h-3 w-3" />
                    {c.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="overflow-hidden rounded-lg border border-slate-700/50">
          <div className="max-h-[480px] overflow-y-auto">
            <table className="w-full">
              <thead className="sticky top-0 bg-slate-800/95 backdrop-blur">
                <tr className="border-b border-slate-700">
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-400">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-400">
                    Event
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-400">
                    User
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-400">
                    Time
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {filtered.map((item, index) => {
                  const { source, cfg, Icon, eventText, userLabel, relativeTime } =
                    renderEventContent(item, index);
                  return (
                    <tr key={`${source}-${item.id || index}`} className="hover:bg-slate-700/30">
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${cfg.bgClass} ${cfg.textClass}`}
                        >
                          <Icon className="h-3 w-3" />
                          {cfg.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-200">{eventText}</td>
                      <td className="px-4 py-3 text-xs text-slate-400">{userLabel || '—'}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">{relativeTime}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  if (viewMode === 'cards') {
    return (
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          {live && (
            <span className="flex items-center gap-1.5 rounded-full bg-green-900/40 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-green-400">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-green-400" />
              Live
            </span>
          )}
          <span className="text-xs text-slate-500">{filtered.length} events</span>
          {sources.length > 1 && (
            <div className="flex flex-wrap gap-1.5">
              <button
                onClick={() => setFilterSource('all')}
                className={`rounded-full px-2.5 py-1 text-[10px] font-medium ${filterSource === 'all' ? 'bg-slate-600 text-white' : 'bg-slate-800/50 text-slate-500 hover:bg-slate-700'}`}
              >
                All
              </button>
              {sources.map(src => {
                const c = getEventConfig(src);
                return (
                  <button
                    key={src}
                    onClick={() => setFilterSource(src)}
                    className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-medium ${
                      filterSource === src
                        ? `${c.bgClass} ${c.textClass}`
                        : 'bg-slate-800/50 text-slate-500 hover:bg-slate-700'
                    }`}
                  >
                    <c.icon className="h-3 w-3" />
                    {c.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((item, index) => {
            const { source, cfg, Icon, eventText, userLabel, relativeTime } = renderEventContent(
              item,
              index
            );
            return (
              <motion.div
                key={`${source}-${item.id || index}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.02 }}
                className="rounded-lg border border-slate-700/50 bg-slate-800/40 p-4 transition-colors hover:border-slate-600/50"
              >
                <div className="flex items-start justify-between gap-2">
                  <span
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${cfg.bgClass} ${cfg.textClass}`}
                  >
                    <Icon className="h-3 w-3" />
                    {cfg.label}
                  </span>
                  <span className="text-xs text-slate-500">{relativeTime}</span>
                </div>
                <p className="mt-2 text-sm font-medium text-slate-100">{eventText}</p>
                {userLabel && <p className="mt-1 text-xs text-slate-500">{userLabel}</p>}
              </motion.div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Live indicator + filters */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {live && (
            <span className="flex items-center gap-1.5 rounded-full bg-green-900/40 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-green-400">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-green-400" />
              Live
            </span>
          )}
          <span className="text-xs text-slate-500">
            {filtered.length} event{filtered.length !== 1 ? 's' : ''}
          </span>
        </div>
        {sources.length > 1 && (
          <div className="flex flex-wrap gap-1.5">
            <button
              onClick={() => setFilterSource('all')}
              className={`rounded-full px-2.5 py-1 text-[10px] font-medium transition-colors ${
                filterSource === 'all'
                  ? 'bg-slate-600 text-white'
                  : 'bg-slate-800/50 text-slate-500 hover:bg-slate-700 hover:text-slate-300'
              }`}
            >
              All
            </button>
            {sources.map(src => {
              const cfg = getEventConfig(src);
              return (
                <button
                  key={src}
                  onClick={() => setFilterSource(src)}
                  className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-medium transition-colors ${
                    filterSource === src
                      ? `${cfg.bgClass} ${cfg.textClass}`
                      : 'bg-slate-800/50 text-slate-500 hover:bg-slate-700 hover:text-slate-300'
                  }`}
                >
                  <cfg.icon className="h-3 w-3" />
                  {cfg.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Timeline */}
      <ol className="relative space-y-3">
        <div className="absolute bottom-2 left-[11px] top-2 w-px bg-slate-700/80" aria-hidden />
        <AnimatePresence mode="popLayout">
          {filtered.map((item, index) => {
            const source = item.source || 'unknown';
            const cfg = getEventConfig(source);
            const Icon = cfg.icon;
            const ts = item.createdAt ?? item.timestamp ?? item.affectedAt;
            const date = toDate(ts);
            const relativeTime = formatRelativeTime(date);
            const absoluteTime = date
              ? date.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'medium' })
              : 'Unknown';

            const eventText =
              item.action ||
              item.eventType ||
              item.message ||
              item.reason ||
              (item.previousStage && item.newStage
                ? `${item.previousStage} → ${item.newStage}`
                : null) ||
              (item.viewerName ? `Viewed by ${item.viewerName}` : null) ||
              'Activity event';

            const userLabel =
              item.userEmail ||
              item.email ||
              item.viewerEmail ||
              (item.userId ? `User ${item.userId.slice(0, 8)}…` : null);

            const hasDetails =
              item.details ||
              item.metadata ||
              item.reason ||
              (item.collection && item.userEmail) ||
              item.userAgent;

            const itemKey = `${source}-${item.id || index}`;
            const isExpanded = expandedId === itemKey;

            return (
              <motion.li
                key={`${source}-${item.id || index}`}
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 12 }}
                transition={{ delay: index * 0.02 }}
                className="relative flex gap-4"
              >
                <div
                  className={`relative z-10 mt-2 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 border-slate-800 ${cfg.dotClass}`}
                >
                  <Icon className="h-3 w-3 text-white" />
                </div>
                <div className="min-w-0 flex-1 rounded-lg border border-slate-700/50 bg-slate-800/40 transition-colors hover:border-slate-600/50 hover:bg-slate-800/60">
                  <div
                    className="flex cursor-default flex-wrap items-start justify-between gap-2 p-4"
                    onClick={() => hasDetails && setExpandedId(isExpanded ? null : itemKey)}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${cfg.bgClass} ${cfg.textClass}`}
                        >
                          <Icon className="h-3 w-3" />
                          {cfg.label}
                        </span>
                        {item.severity && (
                          <span
                            className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${
                              ['high', 'critical'].includes((item.severity || '').toLowerCase())
                                ? 'bg-red-900/50 text-red-300'
                                : 'bg-slate-700 text-slate-400'
                            }`}
                          >
                            {item.severity}
                          </span>
                        )}
                      </div>
                      <p className="mt-2 text-sm font-medium text-slate-100">{eventText}</p>
                      <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-0.5 text-xs text-slate-500">
                        {userLabel && (
                          <span className="flex items-center gap-1">
                            <HiUserCircle className="h-3.5 w-3.5" />
                            {userLabel}
                          </span>
                        )}
                        {item.route && (
                          <span className="font-mono text-slate-500">{item.route}</span>
                        )}
                        <span className="flex items-center gap-1" title={absoluteTime}>
                          <HiClock className="h-3.5 w-3.5" />
                          {relativeTime}
                        </span>
                      </div>
                    </div>
                    {hasDetails && (
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          setExpandedId(isExpanded ? null : itemKey);
                        }}
                        className="shrink-0 rounded p-1.5 text-slate-500 transition-colors hover:bg-slate-700 hover:text-slate-300"
                        aria-label={isExpanded ? 'Collapse' : 'Expand'}
                      >
                        {isExpanded ? (
                          <HiChevronUp className="h-4 w-4" />
                        ) : (
                          <HiChevronDown className="h-4 w-4" />
                        )}
                      </button>
                    )}
                  </div>

                  <AnimatePresence>
                    {isExpanded && hasDetails && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="border-t border-slate-700/50 px-4 py-3">
                          <dl className="space-y-2 text-xs">
                            {item.reason && (
                              <div>
                                <dt className="font-semibold text-slate-500">Reason</dt>
                                <dd className="mt-0.5 text-slate-300">{item.reason}</dd>
                              </div>
                            )}
                            {item.collection && (
                              <div>
                                <dt className="font-semibold text-slate-500">Collection</dt>
                                <dd className="mt-0.5 text-slate-300">{item.collection}</dd>
                              </div>
                            )}
                            {item.details && (
                              <div>
                                <dt className="font-semibold text-slate-500">Details</dt>
                                <dd className="mt-0.5 text-slate-300">
                                  {typeof item.details === 'string'
                                    ? item.details
                                    : JSON.stringify(item.details, null, 2)}
                                </dd>
                              </div>
                            )}
                            {item.metadata && Object.keys(item.metadata).length > 0 && (
                              <div>
                                <dt className="font-semibold text-slate-500">Metadata</dt>
                                <dd className="mt-0.5 font-mono text-slate-400">
                                  <pre className="whitespace-pre-wrap break-words">
                                    {JSON.stringify(item.metadata, null, 2)}
                                  </pre>
                                </dd>
                              </div>
                            )}
                            {item.userAgent && (
                              <div>
                                <dt className="font-semibold text-slate-500">User Agent</dt>
                                <dd className="mt-0.5 truncate text-slate-400">{item.userAgent}</dd>
                              </div>
                            )}
                          </dl>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.li>
            );
          })}
        </AnimatePresence>
      </ol>
    </div>
  );
};

export default ActivityTimeline;
